import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { formatDate, statusClass } from "@/lib/format";
import { ArrowLeft, FileText, Target, ListChecks, X, Milestone, TriangleAlert } from "lucide-react";

export default function PRDDetail() {
  const { id } = useParams();
  const [prd, setPrd] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/prds/${id}`)
      .then((r) => setPrd(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [id]);

  if (err) {
    return (
      <div className="border border-red-900/60 bg-red-950/20 px-4 py-3 font-mono text-xs text-red-300">
        {err}
      </div>
    );
  }
  if (!prd) return <div className="font-mono text-xs text-neutral-500"><span className="cursor-blink">LOADING</span></div>;

  return (
    <div data-testid={TID.prdDetail} className="space-y-6">
      <Link
        to="/app/prds"
        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500 hover:text-[#EDEDED]"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
        Torna a PRD
      </Link>

      <section className="border border-[#1F1F1F] bg-[#0C0C0C]">
        <header className="border-b border-[#1F1F1F] px-5 py-4 flex items-start justify-between bg-[#141414]">
          <div>
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500 flex items-center gap-2">
              <FileText className="w-3 h-3 text-[#0055FF]" strokeWidth={1.5} />
              prd · {prd.id} · v{prd.version}
            </div>
            <h2 className="font-heading uppercase text-2xl tracking-tight mt-1">
              {prd.client_name} · {prd.project_name}
            </h2>
          </div>
          <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-1 ${statusClass(prd.status)}`}>
            {prd.status}
          </span>
        </header>

        <div className="px-5 py-3 border-b border-[#1F1F1F] font-mono text-[11px] text-neutral-500 flex flex-wrap gap-x-6 gap-y-1">
          <div>lead · <span className="text-neutral-300">{prd.lead_id}</span></div>
          <div>generato · <span className="text-neutral-300">{formatDate(prd.created_at)}</span></div>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ListBlock title="Obiettivi" icon={Target} items={prd.objectives} />
        <ListBlock title="Scope IN" icon={ListChecks} items={prd.scope_in} accent="#EDEDED" />
        <ListBlock title="Scope OUT" icon={X} items={prd.scope_out} accent="#5C5C5C" muted />
        <ListBlock title="Rischi & mitigazioni" icon={TriangleAlert} items={prd.risks} accent="#F5A623" />
      </div>

      <section className="border border-[#1F1F1F] bg-[#0C0C0C]">
        <header className="border-b border-[#1F1F1F] px-4 py-3 flex items-center gap-2 bg-[#141414]">
          <Milestone className="w-3.5 h-3.5 text-neutral-500" strokeWidth={1.5} />
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400">
            Milestone
          </span>
          <span className="font-mono text-[10px] text-neutral-600 ml-auto">{prd.milestones.length}</span>
        </header>
        <ul className="divide-y divide-[#1F1F1F]">
          {prd.milestones.map((m, i) => (
            <li key={i} className="px-5 py-4 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
              <div>
                <div className="font-heading uppercase tracking-tight text-lg">{m.name}</div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-[#0055FF] mt-1">
                  settimane {m.weeks}
                </div>
              </div>
              <ul className="space-y-1 text-sm text-neutral-300">
                {m.deliverables.map((d, k) => (
                  <li key={k} className="flex items-start gap-2">
                    <span className="font-mono text-[10px] text-neutral-600 mt-1 shrink-0">→</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ListBlock({ title, icon: Icon, items, accent = "#EDEDED", muted = false }) {
  return (
    <section className="border border-[#1F1F1F] bg-[#0C0C0C]">
      <header className="border-b border-[#1F1F1F] px-4 py-3 flex items-center gap-2 bg-[#141414]">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} strokeWidth={1.5} />
        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400">
          {title}
        </span>
        <span className="ml-auto font-mono text-[10px] text-neutral-600">{items.length}</span>
      </header>
      <ul className="p-4 space-y-2">
        {items.length === 0 ? (
          <li className="font-mono text-xs text-neutral-500">—</li>
        ) : (
          items.map((it, i) => (
            <li key={i} className={`text-sm flex items-start gap-2 ${muted ? "text-neutral-500" : "text-neutral-200"}`}>
              <span className="font-mono text-[10px] text-neutral-600 mt-1 shrink-0" style={{ color: accent }}>·</span>
              <span>{it}</span>
            </li>
          ))
        )}
      </ul>
    </section>
  );
}
